/**
 * API 응답 → 시안이 기대하는 형태로 변환.
 * 시안 컴포넌트를 건드리지 않고 데이터만 실제 값으로 바꾸기 위한 경계층이다.
 */
import * as T from './time.js';

const d = (iso) => (iso ? new Date(iso) : null);

/** MM-DD(앱 시간대) */
export const mdLabel = (iso) => (iso ? T.md(iso).replace('/', '-') : '');

/** 오늘/내일/MM/DD — 업무 기한 표기(앱 시간대의 날) */
export const dueLabel = (iso) => {
  if (!iso) return '-';
  const diff = T.daysBetween(T.todayYmd(), T.ymd(iso));
  if (diff === 0) return '오늘';
  if (diff === 1) return '내일';
  if (diff === -1) return '어제';
  return T.md(iso);
};

/** 상대 시각 — 알림 표기 */
export const agoLabel = (iso) => {
  const t = d(iso); if (!t) return '';
  const m = Math.floor((Date.now() - t.getTime()) / 60000);
  if (m < 1) return '방금';
  if (m < 60) return `${m}분 전`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}시간 전`;
  const day = Math.floor(h / 24);
  return day === 1 ? '어제' : `${day}일 전`;
};

/** 시:분 → 소수 시각 (예약 화면이 9, 10.5 형태를 쓴다 — 앱 시간대) */
export const toDecimalHour = (iso) => (iso ? T.hourOf(iso) : 0);

export const mapMe = (u) => ({
  id: u.id, name: u.displayName, email: u.email,
  dept: u.departmentName ?? '', title: u.title ?? '',
  initials: (u.displayName ?? '?')[0], role: u.roles?.[0] ?? 'user', roles: u.roles ?? []
});

export const mapDepts = (rows) => rows.map((r) => ({
  id: r.id, parent: r.parent_id, name: r.name, code: r.code,
  manager: r.manager_name ?? '-', count: r.member_count
}));

export const mapPeople = (rows) => rows.map((r) => ({
  id: r.id, name: r.display_name, title: r.title ?? '', dept: r.department_name ?? '', deptId: r.department_id ?? null,
  email: r.email, phone: r.mobile_phone || r.business_phone || '-', status: r.status ?? r.employment_status,
  emp: r.employee_no ?? '-', joined: r.joined_at ? String(r.joined_at).slice(0, 10) : '-'
}));

/** YYYY-MM-DD HH:mm — 앱 시간대로(서버는 UTC ISO 문자열로 준다 — 문자열을 잘라 쓰면 UTC 시각이 보인다) */
export const dateTimeLabel = (iso) => (iso ? T.dateTime(iso) : '');

/** 결재선 줄 종류 표시(그 밖은 '결재') */
const LINE_ROLE = { agree: '합의' };
/** 수신자 종류 표시 */
const RECIPIENT_ROLE = { cc: '참조', view: '열람' };

/** 양식 항목 값을 화면 글로 — 종류는 서버 양식 정의(money 원 단위 정수, period {start,end}) */
export const formatFieldValue = (type, value) => {
  if (value === undefined || value === null || value === '') return '';
  if (type === 'money') return '₩' + Number(value).toLocaleString('ko-KR');
  if (type === 'number') return Number(value).toLocaleString('ko-KR');
  if (type === 'period') return value.start === value.end ? value.start : `${value.start} ~ ${value.end}`;
  return String(value);
};

/** 결재 목록 행 — 내 차례(myTurn)·현재 결재자는 서버가 계산한 값이다(화면에서 이름으로 추측하지 않는다) */
export const mapApprovals = (rows) => rows.map((r) => ({
  id: r.id, docNo: r.doc_no ?? '',
  form: r.form_name, title: r.title, drafterId: r.drafter_user_id, drafter: r.drafter_name,
  dept: r.department_name ?? '', amount: r.summary ? formatFieldValue(r.summary.type, r.summary.value) : '',
  status: r.status, step: r.line_done ?? 0, total: r.line_total ?? 0,
  current: r.current_approver_name ?? '', currentKind: r.current_kind ?? null, myTurn: r.my_turn === true,
  turnFor: r.turn_for_name ?? '',   // 지금 내 차례가 대리 차례면 원 결재자 이름
  subject: r.subject_type ?? null,  // 다른 모듈의 결재 대상(휴가 신청 — 휴가 화면에서 만든 문서)
  submittedAt: dateTimeLabel(r.submitted_at), updatedAt: dateTimeLabel(r.updated_at),
  urgent: false
}));

/** 상세의 행동 — 서버가 준 것만 true(모르는 값·빠진 값은 false) */
const DETAIL_ACTIONS = ['approve', 'reject', 'withdraw', 'edit', 'submit', 'delete', 'copy', 'comment'];

/**
 * 타임라인 — 처리 기록(상신·승인/합의·반려/반대·회수)과 댓글을 시간순으로(같은 시각이면 처리 기록 먼저).
 * kind는 처리한 줄의 종류(합의 줄이면 승인·반려를 합의·반대로 읽는다).
 */
export const approvalTimeline = (history, comments) => [
  ...history.map((h) => ({
    type: 'action', id: h.id, action: h.action, kind: h.line_kind ?? null, name: h.actor_name, onBehalfOfName: h.on_behalf_of_name ?? '',
    comment: h.comment ?? '', time: h.created_at, at: dateTimeLabel(h.created_at)
  })),
  ...comments.map((c) => ({
    type: 'comment', id: c.id, name: c.author_name, body: c.body ?? '', deleted: Boolean(c.deleted_at),
    canDelete: c.actions?.delete === true, time: c.created_at, at: dateTimeLabel(c.created_at)
  }))
].sort((a, b) => new Date(a.time) - new Date(b.time) || (a.type === b.type ? 0 : a.type === 'action' ? -1 : 1));

/**
 * 결재 상세 — 버튼은 서버가 준 actions로만, 결재선 줄의 처리 시각·의견은 서버가 줄에 붙인 값으로(이름으로 짝짓지 않는다).
 * timeline = 처리 기록 + 댓글 시간순(행동 종류별 이름표는 화면이 붙인다).
 * schema·values는 초안 고치기 화면이 쓴다 — 초안은 만들 때의 양식 사본(form_schema)으로 칸을 그린다.
 */
export const mapApprovalDetail = ({ document: doc, lines, recipients = [], history, comments = [], actions, turn }) => {
  // 항목은 문서가 가진 양식 사본(form_schema)대로 — 양식이 나중에 바뀌어도 이 문서는 그때 정의로 보인다
  const fields = (doc.form_schema?.fields ?? []).map((f) => ({
    key: f.key, label: f.label, type: f.type, summary: Boolean(f.summary), text: formatFieldValue(f.type, doc.values?.[f.key])
  }));
  return {
  id: doc.id, docNo: doc.doc_no ?? '', form: doc.form_name, formId: doc.form_id, title: doc.title, subject: doc.subject_type ?? null,
  drafterId: doc.drafter_user_id, drafter: doc.drafter_name, dept: doc.department_name ?? '',
  fields, amount: fields.find((f) => f.summary)?.text ?? '', status: doc.status,
  schema: doc.form_schema?.fields ?? [], values: doc.values ?? {},
  submittedAt: dateTimeLabel(doc.submitted_at), updatedAt: dateTimeLabel(doc.updated_at), urgent: false,
  line: lines.map((l) => ({
    id: l.id, userId: l.approver_user_id, name: l.approver_name, kind: l.kind ?? 'approve',
    // 그 줄을 처리한 사람이 결재자와 다르면 대리인
    standIn: l.acted_by && l.acted_by !== l.approver_user_id ? l.acted_by_name : '',
    role: LINE_ROLE[l.kind] ?? '결재', state: l.status,
    at: l.acted_at ? dateTimeLabel(l.acted_at).slice(5) : undefined, actedAt: l.acted_at ? dateTimeLabel(l.acted_at) : ''
  })),
  recipients: recipients.map((r) => ({
    userId: r.user_id, name: r.name, kind: r.kind, role: RECIPIENT_ROLE[r.kind] ?? r.kind,
    notified: Boolean(r.notified_at), readAt: r.read_at ? dateTimeLabel(r.read_at) : ''
  })),
  timeline: approvalTimeline(history, comments),
  actions: Object.fromEntries(DETAIL_ACTIONS.map((a) => [a, actions?.[a] === true])),
  // 지금 내가 처리할 줄 — 버튼 이름(승인/반려·합의/반대)은 이 종류로 정한다(사람 id로 줄을 찾지 않는다)
  turn: turn ? { lineId: turn.lineId, kind: turn.kind, onBehalfOf: turn.onBehalfOf ?? null } : null
  };
};

/** 'HH:MM'(끝은 '24:00'까지) → 소수 시각 */
export const hourOf = (hhmm) => { const [h, m] = String(hhmm).split(':').map(Number); return h + m / 60; };

/** 설비 — 분류·이용 요일(ISO 1=월 … 7=일)·하루 시간대(소수 시각)·최대 길이(분)는 서버 값 그대로(화면은 이 값으로 시간표를 그린다) */
export const mapRooms = (rows) => rows.map((r) => ({
  id: r.id, name: r.name, type: r.type, cap: r.capacity ?? 0, loc: r.location ?? '', description: r.description ?? '',
  openDays: r.open_days ?? [], open: hourOf(r.open_time ?? '00:00'), close: hourOf(r.close_time ?? '24:00'),
  openTime: r.open_time, closeTime: r.close_time, maxMinutes: r.max_minutes ?? null, approvalRequired: r.approval_required === true,
  managerId: r.manager_user_id ?? null, manager: r.manager_name ?? ''
}));

/**
 * 그날(day = YYYY-MM-DD, 앱 시간대 달력)에 걸친 예약 — 시각은 그날 안으로 자른다(여러 날 예약은 그날 0~24시로 보이고 before·after로 표시).
 * '내 예약'은 예약자 id로(이름이 같은 사람이 있을 수 있다 — 계획 문서 D3). series = 반복 예약의 회차(떼어 낸 회차는 단독 예약 — 2-3d)
 */
export const mapBookings = (rows, meId, day) => {
  const dayStart = Date.parse(T.zonedIso(day, 0)), dayEnd = Date.parse(T.zonedIso(T.addDays(day, 1), 0));
  return rows.map((r) => {
    const s = Date.parse(r.starts_at), e = Date.parse(r.ends_at);
    return {
      id: r.id, room: r.facility_id, status: r.status,
      start: s <= dayStart ? 0 : (s - dayStart) / 3_600_000, end: e >= dayEnd ? 24 : (e - dayStart) / 3_600_000,
      before: s < dayStart, after: e > dayEnd, startsAt: r.starts_at, endsAt: r.ends_at,
      title: r.title, by: r.requester_name, mine: r.requester_id === meId, series: Boolean(r.series_id) && !r.series_detached,
      attendees: (r.attendees ?? []).map((a) => a.name)
    };
  });
};

/**
 * 홈이 쓰는 휴가 요약(LEAVE). 대표 잔여 = 서버가 그 해 총 일수(발생 + 조정)가 많은 순으로 준 첫 잔여(종류 코드로 고르지 않는다).
 * 휴가 화면은 이 값을 쓰지 않고 서버에서 직접 받는다.
 */
export const mapLeave = (balance, requests) => {
  const primary = (balance.balances ?? [])[0];
  return {
    balance: {
      name: primary?.name ?? '', granted: primary ? primary.grantedDays + primary.adjustedDays : 0,
      used: primary?.usedDays ?? 0, pending: requests.filter((r) => r.status === 'submitted').length
    },
    types: (balance.balances ?? []).map((b) => ({ code: b.leaveTypeCode, name: b.name, remain: b.remainingDays }))
  };
};

export const mapPosts = (rows) => rows.map((r) => ({
  id: r.id, boardId: r.board_id, board: r.board_name, title: r.title, author: r.author_name,
  dept: r.author_department ?? '', at: mdLabel(r.created_at),
  pinned: r.is_pinned, important: r.importance === 'high', read: r.is_read, views: r.read_count ?? 0,
  categoryId: r.category_id ?? null, category: r.category_name ?? ''
}));

export const mapTasks = (rows) => rows.map((r) => ({
  id: r.id, title: r.title, status: r.status, due: dueLabel(r.due_at),
  priority: r.priority, assignee: r.assignee_name ?? '', requester: r.requester_name ?? '', progress: r.progress, tag: r.tag ?? '',
  canUpdate: r.actions?.update === true   // 서버가 정한 진행 바꾸기 권한(담당자·등록자·관리자 — 계획 문서 2-5)
}));

/** 아이콘은 이벤트 종류의 앞부분(approval.request → approval). 여는 곳은 서버가 준 target(외부 채널 링크와 같은 값 — 계획 문서 5.2 2-7 결정 1) */
const NOTIF_ICON = {
  approval: 'file-check', booking: 'calendar', task: 'user-plus', notice: 'megaphone', leave: 'umbrella', platform: 'shield'
};

export const mapNotifs = (rows) => rows.map((r) => {
  const kind = r.event_type ? r.event_type.split('.')[0] : null;
  return {
    id: r.id, icon: NOTIF_ICON[kind] ?? 'bell', kind, channel: r.channel,
    title: r.title, body: r.body, at: agoLabel(r.created_at),
    unread: !r.read_at, target: r.target ?? null
  };
});
